import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, vShow as _vShow, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "table-responsive mb-2" }
const _hoisted_2 = { class: "table table-striped align-middle" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 1,
  class: "text-nowrap"
}
const _hoisted_5 = { class: "numeric" }
const _hoisted_6 = {
  key: 2,
  class: "numeric"
}
const _hoisted_7 = { key: 3 }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "form-check form-switch" }
const _hoisted_10 = ["id", "value", "disabled"]
const _hoisted_11 = {
  key: 1,
  class: "text-nowrap"
}
const _hoisted_12 = { class: "fst-italic" }
const _hoisted_13 = { class: "numeric" }
const _hoisted_14 = {
  key: 2,
  class: "numeric"
}
const _hoisted_15 = { key: 3 }
const _hoisted_16 = {
  key: 0,
  class: "row"
}
const _hoisted_17 = {
  key: 1,
  class: "row mt-3"
}
const _hoisted_18 = { class: "col d-flex align-items-center" }
const _hoisted_19 = { class: "text-muted align-middle mb-0" }
const _hoisted_20 = {
  key: 2,
  class: "mt-3"
}
const _hoisted_21 = { class: "col border-top pt-3" }
const _hoisted_22 = { class: "text-muted" }
const _hoisted_23 = { class: "d-grid gap-2 d-sm-block" }
const _hoisted_24 = {
  key: 0,
  class: "alert alert-info mb-2 mt-3"
}
const _hoisted_25 = {
  key: 1,
  class: "alert alert-secondary mb-2 mt-3"
}
const _hoisted_26 = { key: 4 }

import { ref, onMounted, computed, PropType } from "vue";
import { PensionPot } from "@/guiide/ts/pensionPot";
import Modal from "@/guiide/components/common/Modal.vue";
import PensionPotEditor from "@/guiide/components/assets/PensionPotEditor.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import AddAsset from "@/guiide/components/assets/AddAsset.vue";
import EmptyAsset from "@/guiide/components/assets/EmptyAsset.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import PermissionService from "@/guiide/ts/permissionService";
import Constants from "@/guiide/ts/constants";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import ThreeDotMenu from "@/guiide/components/common/ThreeDotMenu.vue";
import { useStore } from "vuex";
import { formatter } from "../../../shared/ts/formatter";
import { app } from "@/guiide/ts/app";
import Monitoring from "@/guiide/ts/monitoring/monitoring";
import { GuaranteedIncome } from "@/guiide/ts/guaranteedIncome";
import View from "@/guiide/ts/view";
import AnnuityType from "@/guiide/ts/annuityType";
import { useUIContent } from "@/guiide/ts/configuration/useUIContent";
import { getValuationDateDescription } from "@/guiide/ts/utilities";


export default /*@__PURE__*/_defineComponent({
  __name: 'PensionPotsViewer',
  props: /*@__PURE__*/_mergeModels({
    showValuation: { type: Boolean, default: true },
    showEstRetirementValue: { type: Boolean, required: false, default: true },
    showEditButtons: { type: Boolean, required: false, default: true },
    showAddButton: { type: Boolean, required: false, default: true },
    showAdvice: { type: Boolean, required: false, default: true },
    showHelp: { type: Boolean, required: false, default: true },
    title: { type: String, required: false, default: "Your defined contribution pension pots"},
    selectionEnabled: { type: Boolean, required: false, default: false },
    selectedColumnTitle: { type: String, required: false, default: "Included" },
    showPathlinesAlert: { type: Boolean, required: false, default: false },
    showPresentTotalValue: { type: Boolean, required: false, default: false },
    selectablePensionPotIds: { type: Array as PropType<Array<number>>, default: () => []},
    currentValueHelpText: { type: Array as PropType<Array<string>>, default: () => HelpText.PensionPotsCurrentValue()}
}, {
    "selectedPensionPotIds": { default: () => [] },
    "selectedPensionPotIdsModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(['guaranteed-income-change-required'], ["update:selectedPensionPotIds"]),
  setup(__props, { emit: __emit }) {

const props = __props;

const selectedPensionPotIds = _useModel<number[]>(
    __props, 'selectedPensionPotIds'
);

const emit = __emit;

const store = useStore();
const { textArray, htmlArray } = useUIContent();

const potModal = ref(null);
const modalTitle = ref("");
const pensionPotToEdit = ref(ObjectBuilder.createPensionPot(Constants.NewId, "", 0, 0, 0, 0, false, 0, 0, true, true, true));
const selectedPensionPots = ref([]);

const addAssetButton = ref<HTMLElement>(null);
const addAssetComponent = ref<InstanceType<typeof AddAsset> | null>(null);

onMounted(() => {
  selectedPensionPots.value = getPensionPots.value;
});

const hasPensionPots = computed<boolean>(() => {
  return store.state.retiree?.pensionPots.pots.length > 0;
});

const getPensionPots = computed<Array<PensionPot>>(() => {
  return (
    store.state.retiree?.pensionPots.pots ?? new Array<PensionPot>()
  );
});

const canLinkLiveValues = computed<boolean>(() => {
  if (store.state.view == View.Haves) {
    return store.state.configuration.canLinkLiveValuesFromHaves;
  } else if (
    store.state.view == View.Dashboard ||
    store.state.view == View.GuiidePlus
  ) {
    return store.state.configuration.canLinkLiveValuesFromDashboard;
  } else {
    return false;
  }
});

const isLinkedToLiveValues = computed<boolean>(() => {
  return store.state.retiree?.isLinkedToLiveValues ?? false;
});

const linkMoreLiveFundsAdviceText = computed<string>(() => {
  return store.state.configuration.liveValuesLinkMoreFundsAdviceText;
});

const showCurrentContributionHelp = computed<boolean>(() => {
  return store.state.configuration
    .dashboardShowCurrentContributionHelp;
});

const showLumpSumInfo = computed<boolean>(() => {
  if (store.state.retiree == null) {
    return false;
  }
  if (store.state.retiree.alreadyDrawingPension) {
    return false;
  }
  return (
    store.state.retiree.requirements.takingTaxFreeAsLumpSum && hasPensionPots.value
  );
});

const lumpSumInfo = computed<string>(() => {
  if (!showLumpSumInfo.value) {
    return "";
  }

  const lumpSumAmount = store.state.retiree.pensionPots.lumpSumValueAtRetirement;
  let lumpSumAmountFormatted: string;
  let reductionStatement: string;

  if (lumpSumAmount < Constants.TaxFreeCashLimit) {
    lumpSumAmountFormatted = formatter.formatCurrency(lumpSumAmount);
    reductionStatement = `The total estimated figure at retirement will immediately be reduced
      by ${lumpSumAmountFormatted} as you have chosen to take lump sum tax free cash.`;
  } else {
    lumpSumAmountFormatted = formatter.formatCurrency(Constants.TaxFreeCashLimit);
    reductionStatement = `The total estimated figure at retirement will immediately be reduced
      by ${lumpSumAmountFormatted} (the current maximum tax free cash possible) as you have 
      chosen to take lump sum tax free cash.`;
  }

  return `${reductionStatement} We assume you will spend this, but you can add this
      back into other cash below at your retirement date if not.`
});

const showGuaranteedIncomeInfo = computed<boolean>(() => {
  if (!store.state.configuration.guaranteedIncomeEnabled) {
    return false;
  }
  return store.state.retiree?.guaranteedIncome.subscribed ?? false;
});

const annuityType = computed<AnnuityType>(() => {
  return store.state.configuration.annuityType;
});

const guaranteedIncomeText = computed<string>(() => {
  if (!showGuaranteedIncomeInfo.value) {
    return "";
  }

  const guaranteedIncome = store.state.retiree.guaranteedIncome as GuaranteedIncome;
  const allocationAmount = formatter.formatCurrency(guaranteedIncome.allocationAmount);
  const annualIncome = formatter.formatCurrency(guaranteedIncome.annualIncome);
  const subscriptionDateFormatted = formatter.monthAndYearDate(store.state.retiree.guaranteedIncome.subscriptionDate);

  let remaining = "";
  if (store.state.retiree.requirements.takingTaxFreeAsLumpSum) {
    remaining = "remaining ";
  }

  const pot = getPensionPots.value.length == 1 ? "pot" : "pots";

  switch (annuityType.value) {
    case AnnuityType.GuaranteedIncome:
      return (
        `You have chosen to see how ${guaranteedIncome.allocation}% (${allocationAmount}) of your ${remaining}non protected pension ${pot} could be used to buy a guaranteed` +
        ` income paid into your ${pot} from ${subscriptionDateFormatted}. On the illustrative example, it would mean a guaranteed income of ${annualIncome}.`
      );
    case AnnuityType.Traditional:
      return `You have chosen to use ${guaranteedIncome.allocation}% (${allocationAmount}) of your ${remaining} non protected pension ${pot} to model an estimated guaranteed` +
        ` income of ${annualIncome}, which has been added to other income below.`

    default:
      throw new Error("Unknown annuity type.");
  }
});

const estimatedTotalPresentValue = computed<number>(() => {
  if (store.state.retiree == null) {
    return 0;
  } else {
    return getPensionPots.value
      .filter(pp => selectedPensionPotIds.value.includes(pp.id))
      .reduce((total, pp) => total + pp.estimatedValueAtPresent, 0);
  }
});

const estRetirementValueColumnVisible = computed<boolean>(() => {
  if (!props.showEstRetirementValue) {
    return false;
  }

  if (!store.state.retiree) {
    return props.showEstRetirementValue;
  }

  return !store.state.retiree.alreadyDrawingPension;
});

function getPensionPermissions(pensionPot: PensionPot) {
  return PermissionService.getPermissionSet(pensionPot);
}

function onEditFinished() {
  potModal.value.closeModal();
}

function onAdd() {
  modalTitle.value = "Add defined contribution pension pot";
  const pensionPot = ObjectBuilder.createPensionPot(Constants.NewId, "", 0, 0, 0, 0, false, 0, 0, true, true, true);
  pensionPotToEdit.value = pensionPot;

  addAssetButton.value = addAssetComponent.value.getAddButton();

  potModal.value.showModal();
}

function onEdit(id: number) {
  const pensionPot = getPensionPots.value.filter((p) => p.id == id)[0];
  showPensionEditor("Edit defined contribution pension pot", pensionPot);
}

function onView(id: number) {
  const pensionPot = getPensionPots.value.filter((p) => p.id == id)[0];
  showPensionEditor("View defined contribution pension pot", pensionPot);
}

function showPensionEditor(title: string, pensionPot: PensionPot) {
  modalTitle.value = title;
  pensionPotToEdit.value = pensionPot;
  potModal.value.showModal();
}

async function onDelete(id: number) {
  await app.deletePensionPot(id);
}

async function onTransferToPensionLab(event: any) {
  event.preventDefault();
  Monitoring.pensionLabDashboardEvent(store.state.view);
  await app.transferToPensionLab();
}

async function onUnsubscribeGuaranteedIncomeClicked(event: any) {
  event.preventDefault();
  await app.deleteGuaranteedIncome();
}

function isOnlySelectedPot(pensionPotId: number): boolean {
    return selectedPensionPotIds.value.length === 1 && selectedPensionPotIds.value[0] === pensionPotId;
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(SubSection, {
      title: __props.title,
      helpTitle: "Your defined contribution pension pots",
      showHelp: __props.showHelp,
      helpText: _unref(htmlArray)('haves.pension-pots.help')
    }, {
      default: _withCtx(() => [
        _renderSlot(_ctx.$slots, "message"),
        _withDirectives(_createElementVNode("div", _hoisted_1, [
          _createElementVNode("table", _hoisted_2, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                (__props.selectionEnabled)
                  ? (_openBlock(), _createElementBlock("th", _hoisted_3, _toDisplayString(__props.selectedColumnTitle), 1))
                  : _createCommentVNode("", true),
                _cache[9] || (_cache[9] = _createElementVNode("th", null, "Provider", -1)),
                (__props.showValuation)
                  ? (_openBlock(), _createElementBlock("th", _hoisted_4, "Valuation"))
                  : _createCommentVNode("", true),
                _createElementVNode("th", {
                  class: _normalizeClass(["numeric contribution-column", { compact: __props.showValuation && estRetirementValueColumnVisible.value }])
                }, [
                  _cache[6] || (_cache[6] = _createTextVNode(" Personal contrib. ")),
                  (showCurrentContributionHelp.value)
                    ? (_openBlock(), _createBlock(Help, {
                        key: 0,
                        title: "Pension contributions",
                        text: _unref(textArray)('haves.pension-pots.contributions.help')
                      }, null, 8, ["text"]))
                    : _createCommentVNode("", true)
                ], 2),
                _createElementVNode("th", {
                  class: _normalizeClass(["numeric contribution-column", { compact: __props.showValuation && estRetirementValueColumnVisible.value }])
                }, " Employer contrib. ", 2),
                _createElementVNode("th", _hoisted_5, [
                  _cache[7] || (_cache[7] = _createTextVNode(" Est. current value ")),
                  _createVNode(Help, {
                    title: "Estimated current value",
                    text: __props.currentValueHelpText
                  }, null, 8, ["text"])
                ]),
                (estRetirementValueColumnVisible.value)
                  ? (_openBlock(), _createElementBlock("th", _hoisted_6, [
                      _cache[8] || (_cache[8] = _createTextVNode(" Est. at retirement value ")),
                      _createVNode(Help, {
                        title: "Estimated value at retirement",
                        text: _unref(HelpText).PensionPotsEstValAtRetirement()
                      }, null, 8, ["text"])
                    ]))
                  : _createCommentVNode("", true),
                (__props.showEditButtons)
                  ? (_openBlock(), _createElementBlock("th", _hoisted_7))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getPensionPots.value, (pensionPot) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: pensionPot.name
                }, [
                  (__props.selectionEnabled)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                          _withDirectives(_createElementVNode("input", {
                            type: "checkbox",
                            class: "form-check-input todays-money-chk",
                            role: "switch",
                            id: pensionPot.id.toString(),
                            value: pensionPot.id,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedPensionPotIds).value = $event)),
                            disabled: !__props.selectablePensionPotIds.includes(pensionPot.id) || isOnlySelectedPot(pensionPot.id)
                          }, null, 8, _hoisted_10), [
                            [_vModelCheckbox, selectedPensionPotIds.value]
                          ])
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("td", {
                    class: _normalizeClass({'text-nowrap': pensionPot.name.startsWith('Pension Pot')})
                  }, _toDisplayString(pensionPot.name), 3),
                  (__props.showValuation)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_11, [
                        _createTextVNode(_toDisplayString(_unref(formatter).formatCurrency(pensionPot.valuationAmount)) + " ", 1),
                        _createElementVNode("span", _hoisted_12, "(" + _toDisplayString(_unref(getValuationDateDescription)(pensionPot.valuationDate)) + ")", 1)
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("td", {
                    class: _normalizeClass(["numeric contribution-column", { compact: __props.showValuation && estRetirementValueColumnVisible.value }])
                  }, _toDisplayString(_unref(formatter).formatCurrency(pensionPot.personalMonthlyContribution)), 3),
                  _createElementVNode("td", {
                    class: _normalizeClass(["numeric contribution-column", { compact: __props.showValuation && estRetirementValueColumnVisible.value }])
                  }, _toDisplayString(_unref(formatter).formatCurrency(pensionPot.employerMonthlyContribution)), 3),
                  _createElementVNode("td", _hoisted_13, _toDisplayString(_unref(formatter).formatCurrency(pensionPot.estimatedValueAtPresent)), 1),
                  (estRetirementValueColumnVisible.value)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_14, _toDisplayString(_unref(formatter).formatCurrency(pensionPot.estimatedValueAtRetirement)), 1))
                    : _createCommentVNode("", true),
                  (__props.showEditButtons)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_15, [
                        _createVNode(ThreeDotMenu, {
                          id: pensionPot.id,
                          permissions: getPensionPermissions(pensionPot),
                          "aria-purpose": pensionPot.name,
                          onViewClicked: onView,
                          onEditClicked: onEdit,
                          onDeleteClicked: onDelete
                        }, null, 8, ["id", "permissions", "aria-purpose"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ])
          ])
        ], 512), [
          [_vShow, hasPensionPots.value]
        ]),
        _withDirectives(_createElementVNode("div", null, [
          _createVNode(EmptyAsset, { name: "defined contribution pension pots" })
        ], 512), [
          [_vShow, !hasPensionPots.value]
        ]),
        (__props.showAddButton)
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
              _createVNode(AddAsset, {
                ref_key: "addAssetComponent",
                ref: addAssetComponent,
                name: "pot",
                longName: "another defined contribution pension pot manually",
                instruction: "Click Add (+) to add more manually.",
                onAddClicked: _cache[1] || (_cache[1] = ($event: any) => (onAdd()))
              }, null, 512)
            ]))
          : _createCommentVNode("", true),
        (__props.showAdvice && showLumpSumInfo.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("p", _hoisted_19, _toDisplayString(lumpSumInfo.value), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.showAdvice && showGuaranteedIncomeInfo.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("p", _hoisted_22, [
                  _createTextVNode(_toDisplayString(guaranteedIncomeText.value) + " ", 1),
                  _createVNode(Help, {
                    title: "Guaranteed income",
                    text: _unref(HelpText).guaranteedIncome(annuityType.value)
                  }, null, 8, ["text"])
                ]),
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn btn-dark me-sm-2",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (onUnsubscribeGuaranteedIncomeClicked($event)))
                  }, "Remove"),
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn btn-primary",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('guaranteed-income-change-required')))
                  }, "Change")
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.showAdvice && canLinkLiveValues.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              (!isLinkedToLiveValues.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                    _cache[10] || (_cache[10] = _createElementVNode("i", { class: "bi bi-database-add me-2" }, null, -1)),
                    _cache[11] || (_cache[11] = _createTextVNode(" Add live pots via our ")),
                    _createElementVNode("a", {
                      onClick: _cache[4] || (_cache[4] = ($event: any) => (onTransferToPensionLab($event))),
                      href: "",
                      class: "alert-link skin-link me-1"
                    }, "live dashboard setup"),
                    _createVNode(Help, {
                      title: "Live pension pots",
                      text: _unref(HelpText).livePensionPots()
                    }, null, 8, ["text"])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_25, [
                    _cache[12] || (_cache[12] = _createElementVNode("i", { class: "bi bi-info-circle-fill" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(linkMoreLiveFundsAdviceText.value) + " ", 1),
                    _createElementVNode("a", {
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (onTransferToPensionLab($event))),
                      href: "",
                      class: "alert-link"
                    }, "here")
                  ]))
            ], 64))
          : _createCommentVNode("", true),
        (__props.showPresentTotalValue)
          ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
              _createElementVNode("p", null, [
                _cache[13] || (_cache[13] = _createTextVNode(" The total estimated current value of your selected pension pots is ")),
                _createElementVNode("strong", null, _toDisplayString(_unref(formatter).formatCurrency(estimatedTotalPresentValue.value)), 1),
                _cache[14] || (_cache[14] = _createTextVNode(". "))
              ])
            ]))
          : _createCommentVNode("", true),
        _renderSlot(_ctx.$slots, "lower-area")
      ]),
      _: 3
    }, 8, ["title", "showHelp", "helpText"]),
    _createVNode(Modal, {
      title: modalTitle.value,
      ref_key: "potModal",
      ref: potModal,
      idKey: "pensionPot",
      "close-button-aria-label": "Close defined contribution pension pot popup editor",
      returnFocusTo: addAssetButton.value
    }, {
      default: _withCtx(() => [
        _createVNode(PensionPotEditor, {
          ref: "potEditor",
          onEditFinished: onEditFinished,
          "pension-pot": pensionPotToEdit.value
        }, null, 8, ["pension-pot"])
      ]),
      _: 1
    }, 8, ["title", "returnFocusTo"])
  ], 64))
}
}

})